import { useEffect, useMemo, useState, type ChangeEvent } from "react";
import { useAuth } from "@/contexts/AuthContext";
import {
  getAdminCategoryAliases,
  saveCategoryAlias,
  deleteCategoryAlias,
  getAdminCategories,
  getAdminDetailedCategories,
  type CategoryAliasPayload,
} from "@/services/adminService";
import type { AdminCategory, AdminCategoryAlias, AdminDetailedCategory } from "@/types/admin";
import { findBrandedCategorySlug } from "@/utils/categoryMatch";
import { useAdminResource } from "@/pages/portal/admin/hooks/useAdminResource";
import AdminDataError from "@/pages/portal/admin/components/AdminDataError";
import AdminSearchInput from "@/pages/portal/admin/components/AdminSearchInput";
import AdminModal from "@/pages/portal/admin/components/AdminModal";
import {
  adminCard,
  adminTh,
  adminTd,
  adminBtnPrimary,
  adminBtnGhost,
  adminBtnDanger,
  adminIconBtn,
  adminInput,
  adminLabel,
} from "@/pages/portal/admin/config/ui";
import { downloadCsv, parseCsv } from "@/utils/csv";

const EMPTY: CategoryAliasPayload = { azName: "", categorySlug: "", isActive: true };

type ViewMode = "aliases" | "unmapped";

const aliasKey = (value: string): string => value.trim().toLowerCase();

/** One parsed row from an uploaded CSV, with its validation result. */
interface ImportPreviewRow {
  azName: string;
  categorySlug: string;
  isActive: boolean;
  valid: boolean;
  reason?: string;
}

/**
 * Admin — Category aliases.
 *
 * Maps any A–Z index category name (e.g. "Advocates & Lawyers") to a curated
 * branded category page (/category/<slug>) without a code change. Aliases are
 * read publicly by the A–Z page and take priority over the automatic matching,
 * so an admin always wins for the names they care about.
 *
 * The "Unmapped" view surfaces the A–Z names that still have NO branded page
 * (no active alias and no automatic match), so they can be spotted and mapped
 * in bulk.
 */
export default function CategoryAliasesSection() {
  const { accessToken } = useAuth();

  const { data, setData, loading, error, reload } = useAdminResource(
    () => getAdminCategoryAliases(accessToken),
    [] as AdminCategoryAlias[],
    [accessToken],
  );

  // Reference data for the editor: the A–Z names to map and the branded targets.
  const [azCategories, setAzCategories] = useState<AdminDetailedCategory[]>([]);
  const [brandedCategories, setBrandedCategories] = useState<AdminCategory[]>([]);
  const [refsLoaded, setRefsLoaded] = useState(false);

  const [view, setView] = useState<ViewMode>("aliases");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<number | null>(null);
  const [editing, setEditing] = useState<CategoryAliasPayload | null>(null);
  const [editId, setEditId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<AdminCategoryAlias | null>(null);

  // CSV import state.
  const [importOpen, setImportOpen] = useState(false);
  const [importFileName, setImportFileName] = useState("");
  const [importRows, setImportRows] = useState<ImportPreviewRow[]>([]);
  const [importBusy, setImportBusy] = useState(false);
  const [importMessage, setImportMessage] = useState("");

  // Bulk-assign state for the unmapped view.
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkSlug, setBulkSlug] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkMessage, setBulkMessage] = useState("");

  // Load reference lists once the section is opened.
  useEffect(() => {
    let active = true;
    Promise.all([getAdminDetailedCategories(accessToken), getAdminCategories(accessToken)])
      .then(([az, cats]) => {
        if (!active) return;
        setAzCategories(az);
        setBrandedCategories(cats);
      })
      .catch(() => {
        if (active) {
          setAzCategories([]);
          setBrandedCategories([]);
        }
      })
      .finally(() => {
        if (active) setRefsLoaded(true);
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const brandedNameBySlug = useMemo(() => {
    const map = new Map<string, string>();
    for (const c of brandedCategories) map.set(c.slug, c.name);
    return map;
  }, [brandedCategories]);

  /** Existing alias row keyed by (lowercased) A–Z name — used to update, not duplicate. */
  const aliasByName = useMemo(() => {
    const map = new Map<string, AdminCategoryAlias>();
    for (const a of data) map.set(aliasKey(a.azName), a);
    return map;
  }, [data]);

  /** Only ACTIVE aliases decide what the public A–Z page treats as mapped. */
  const activeAliasMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of data) {
      if (a.isActive) map.set(aliasKey(a.azName), a.categorySlug);
    }
    return map;
  }, [data]);

  const azNames = useMemo(() => azCategories.map((c) => c.name), [azCategories]);

  const brandedRef = useMemo(
    () => brandedCategories.map((c) => ({ slug: c.slug, name: c.name })),
    [brandedCategories],
  );

  /**
   * A–Z names that currently resolve to NO branded page — no active alias and
   * no automatic match — so they fall back to a keyword search. Only computed
   * once the branded list is available (otherwise every name would look unmapped).
   */
  const unmapped = useMemo(() => {
    if (!refsLoaded || brandedRef.length === 0) return [] as AdminDetailedCategory[];
    return azCategories.filter(
      (c) => !findBrandedCategorySlug(c.name, brandedRef, activeAliasMap),
    );
  }, [azCategories, brandedRef, activeAliasMap, refsLoaded]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((a) =>
      [a.azName, a.categorySlug, a.categoryName ?? "", brandedNameBySlug.get(a.categorySlug) ?? ""]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [data, search, brandedNameBySlug]);

  const filteredUnmapped = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return unmapped;
    return unmapped.filter((c) => c.name.toLowerCase().includes(q));
  }, [unmapped, search]);

  // What the name would link to WITHOUT any alias (editor hint). Deliberately
  // ignores aliases so it never shows the alias currently being edited.
  const autoMatchForEditing = useMemo(() => {
    const name = editing?.azName?.trim();
    if (!name || brandedCategories.length === 0) return null;
    const slug = findBrandedCategorySlug(name, brandedRef);
    return slug ? { slug, name: brandedNameBySlug.get(slug) ?? slug } : null;
  }, [editing, brandedCategories, brandedRef, brandedNameBySlug]);

  const allFilteredSelected =
    filteredUnmapped.length > 0 && filteredUnmapped.every((c) => selected.has(c.name));
  const selectedCount = selected.size;

  const brandedSlugSet = useMemo(
    () => new Set(brandedCategories.map((c) => c.slug)),
    [brandedCategories],
  );
  const validImportCount = importRows.filter((r) => r.valid).length;
  const invalidImportCount = importRows.length - validImportCount;

  function switchView(next: ViewMode) {
    setView(next);
    setSearch("");
    setBulkMessage("");
    if (next === "aliases") setSelected(new Set());
  }

  function openCreate() {
    setEditing({ ...EMPTY });
    setEditId(null);
  }

  function openEdit(row: AdminCategoryAlias) {
    setEditing({ azName: row.azName, categorySlug: row.categorySlug, isActive: row.isActive });
    setEditId(row.id);
  }

  /** Pre-fill the create editor straight from an unmapped row. */
  function assignOne(name: string) {
    setEditing({ azName: name, categorySlug: "", isActive: true });
    setEditId(aliasByName.get(aliasKey(name))?.id ?? null);
  }

  function setField<K extends keyof CategoryAliasPayload>(key: K, value: CategoryAliasPayload[K]) {
    setEditing((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  function toggleSelect(name: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }

  function toggleSelectAll() {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) filteredUnmapped.forEach((c) => next.delete(c.name));
      else filteredUnmapped.forEach((c) => next.add(c.name));
      return next;
    });
  }

  async function submit() {
    const azName = editing?.azName?.trim();
    const categorySlug = editing?.categorySlug?.trim();
    if (!azName || !categorySlug) return;
    setBusyId(editId ?? -1);
    try {
      await saveCategoryAlias(accessToken, { ...editing, azName, categorySlug }, editId ?? undefined);
      const categoryName = brandedNameBySlug.get(categorySlug) ?? null;
      setData((prev) => {
        const key = aliasKey(azName);
        const idx = prev.findIndex((a) => aliasKey(a.azName) === key);
        if (idx >= 0) {
          const next = [...prev];
          next[idx] = { ...next[idx], azName, categorySlug, categoryName, isActive: !!editing?.isActive };
          return next;
        }
        return [
          ...prev,
          { id: Date.now(), azName, categorySlug, categoryName, isActive: editing?.isActive ?? true },
        ];
      });
      setEditing(null);
    } finally {
      setBusyId(null);
    }
  }

  /** Assign the chosen branded page to every selected unmapped name in one go. */
  async function assignBulk() {
    const slug = bulkSlug.trim();
    if (!slug || selectedCount === 0 || bulkBusy) return;
    setBulkBusy(true);
    setBulkMessage("");
    const names = Array.from(selected);
    const categoryName = brandedNameBySlug.get(slug) ?? null;
    try {
      const results = await Promise.allSettled(
        names.map((azName) =>
          saveCategoryAlias(
            accessToken,
            { azName, categorySlug: slug, isActive: true },
            aliasByName.get(aliasKey(azName))?.id,
          ),
        ),
      );
      const okNames = names.filter((_, i) => results[i].status === "fulfilled");
      if (okNames.length > 0) {
        setData((prev) => {
          const next = [...prev];
          for (const azName of okNames) {
            const key = aliasKey(azName);
            const idx = next.findIndex((a) => aliasKey(a.azName) === key);
            if (idx >= 0) {
              next[idx] = { ...next[idx], azName, categorySlug: slug, categoryName, isActive: true };
            } else {
              next.push({
                id: Date.now() + next.length,
                azName,
                categorySlug: slug,
                categoryName,
                isActive: true,
              });
            }
          }
          return next;
        });
      }
      const failed = names.length - okNames.length;
      setBulkMessage(
        failed > 0
          ? `${okNames.length} assigned, ${failed} failed — try again.`
          : `${okNames.length} ${okNames.length === 1 ? "alias" : "aliases"} created.`,
      );
      setSelected(new Set());
      setBulkSlug("");
    } finally {
      setBulkBusy(false);
    }
  }

  async function toggleActive(row: AdminCategoryAlias) {
    const next = !row.isActive;
    setData((prev) => prev.map((a) => (a.id === row.id ? { ...a, isActive: next } : a)));
    await saveCategoryAlias(accessToken, { isActive: next }, row.id);
  }

  async function confirmDelete() {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    try {
      await deleteCategoryAlias(accessToken, deleteTarget.id);
      setData((prev) => prev.filter((a) => a.id !== deleteTarget.id));
      setDeleteTarget(null);
    } finally {
      setBusyId(null);
    }
  }

  // ── CSV export / import ─────────────────────────────────────────────────────

  function exportAliases() {
    const headers = ["az_name", "category_slug", "is_active"];
    const rows = data.map((a) => [a.azName, a.categorySlug, a.isActive ? "true" : "false"]);
    downloadCsv(`category-aliases-${new Date().toISOString().slice(0, 10)}.csv`, headers, rows);
  }

  function downloadTemplate() {
    downloadCsv(
      "category-aliases-template.csv",
      ["az_name", "category_slug", "is_active"],
      [["Advocates & Lawyers", "", "true"]],
    );
  }

  function openImport() {
    setImportOpen(true);
    setImportRows([]);
    setImportFileName("");
    setImportMessage("");
  }

  function parseActive(value: string): boolean {
    const v = value.trim().toLowerCase();
    return v === "" || ["1", "true", "yes", "y", "active"].includes(v);
  }

  function handleImportFile(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setImportMessage("");
    setImportFileName(file.name);
    file
      .text()
      .then((text) => {
        const table = parseCsv(text);
        if (table.length === 0) {
          setImportRows([]);
          setImportMessage("That file looks empty.");
          return;
        }
        const header = table[0].map((c) => c.trim().toLowerCase());
        const headerKeys = ["az_name", "az name", "name", "category_slug", "slug", "is_active", "active"];
        const hasHeader = header.some((c) => headerKeys.includes(c));
        const bodyRows = hasHeader ? table.slice(1) : table;
        const nameIdx = hasHeader ? header.findIndex((c) => ["az_name", "az name", "name"].includes(c)) : 0;
        const slugIdx = hasHeader ? header.findIndex((c) => ["category_slug", "slug", "category"].includes(c)) : 1;
        const activeIdx = hasHeader ? header.findIndex((c) => ["is_active", "active", "status"].includes(c)) : 2;

        const rows: ImportPreviewRow[] = bodyRows.map((r) => {
          const azName = (nameIdx >= 0 ? r[nameIdx] : r[0])?.trim() ?? "";
          const categorySlug = (slugIdx >= 0 ? r[slugIdx] : r[1])?.trim() ?? "";
          const activeRaw = activeIdx >= 0 ? (r[activeIdx] ?? "") : (r[2] ?? "");
          const isActive = parseActive(activeRaw);
          let valid = true;
          let reason: string | undefined;
          if (!azName) {
            valid = false;
            reason = "Missing A–Z name";
          } else if (!categorySlug) {
            valid = false;
            reason = "Missing category slug";
          } else if (!brandedSlugSet.has(categorySlug)) {
            valid = false;
            reason = `Unknown branded category \"${categorySlug}\"`;
          }
          return { azName, categorySlug, isActive, valid, reason };
        });
        setImportRows(rows);
      })
      .catch(() => setImportMessage("We couldn't read that file. Please try again."));
  }

  async function confirmImport() {
    const rows = importRows.filter((r) => r.valid);
    if (rows.length === 0 || importBusy) return;
    setImportBusy(true);
    setImportMessage("");
    try {
      const results = await Promise.allSettled(
        rows.map((r) =>
          saveCategoryAlias(
            accessToken,
            { azName: r.azName, categorySlug: r.categorySlug, isActive: r.isActive },
            aliasByName.get(aliasKey(r.azName))?.id,
          ),
        ),
      );
      const okRows = rows.filter((_, i) => results[i].status === "fulfilled");
      if (okRows.length > 0) {
        setData((prev) => {
          const next = [...prev];
          for (const r of okRows) {
            const key = aliasKey(r.azName);
            const idx = next.findIndex((a) => aliasKey(a.azName) === key);
            const categoryName = brandedNameBySlug.get(r.categorySlug) ?? null;
            if (idx >= 0) {
              next[idx] = { ...next[idx], azName: r.azName, categorySlug: r.categorySlug, categoryName, isActive: r.isActive };
            } else {
              next.push({ id: Date.now() + next.length, azName: r.azName, categorySlug: r.categorySlug, categoryName, isActive: r.isActive });
            }
          }
          return next;
        });
      }
      const failed = rows.length - okRows.length;
      setImportMessage(
        failed > 0
          ? `${okRows.length} imported, ${failed} failed — try again.`
          : `${okRows.length} ${okRows.length === 1 ? "alias" : "aliases"} imported.`,
      );
      if (failed === 0) {
        setImportRows([]);
        setImportFileName("");
      }
    } finally {
      setImportBusy(false);
    }
  }

  const canSubmit = !!editing?.azName?.trim() && !!editing?.categorySlug?.trim();
  const countLabel =
    view === "aliases"
      ? loading
        ? "Loading…"
        : `${filtered.length} of ${data.length} aliases`
      : refsLoaded
        ? `${filteredUnmapped.length} of ${unmapped.length} unmapped`
        : "Loading…";

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <AdminSearchInput
          value={search}
          onChange={setSearch}
          placeholder={view === "aliases" ? "Search aliases…" : "Search unmapped names…"}
        />

        <div className="inline-flex items-center gap-1 rounded-full border border-background-200 bg-background-100 p-1">
          <button
            type="button"
            onClick={() => switchView("aliases")}
            className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
              view === "aliases"
                ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                : "text-foreground-600 hover:text-foreground-900"
            }`}
          >
            All aliases
          </button>
          <button
            type="button"
            onClick={() => switchView("unmapped")}
            className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs font-semibold transition-colors ${
              view === "unmapped"
                ? "bg-primary-500 text-background-50 dark:text-foreground-950"
                : "text-foreground-600 hover:text-foreground-900"
            }`}
          >
            Unmapped
            {refsLoaded && (
              <span
                className={`rounded-full px-1.5 py-0.5 text-[10px] font-bold ${
                  view === "unmapped"
                    ? "bg-background-50/25 text-background-50 dark:text-foreground-950"
                    : "bg-background-200 text-foreground-700"
                }`}
              >
                {unmapped.length.toLocaleString("en-IN")}
              </span>
            )}
          </button>
        </div>

        <button
          type="button"
          onClick={exportAliases}
          disabled={data.length === 0}
          className={adminBtnGhost}
        >
          <i className="ri-download-2-line text-sm" /> Export CSV
        </button>
        <button type="button" onClick={openImport} className={adminBtnGhost}>
          <i className="ri-upload-2-line text-sm" /> Import CSV
        </button>
        <button type="button" onClick={openCreate} className={adminBtnPrimary}>
          <i className="ri-add-line text-sm" /> New alias
        </button>
        <span className="ml-auto text-xs text-foreground-500">{countLabel}</span>
      </div>

      <div className="mb-5 flex items-start gap-2.5 rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-3 text-xs text-secondary-900">
        <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
          <i className="ri-information-line text-base" />
        </span>
        <p>
          An alias links a name from the <strong>A–Z category list</strong> straight to a{" "}
          <strong>branded category page</strong> (<code className="rounded bg-background-100 px-1 py-0.5">/category/&lt;slug&gt;</code>).
          Aliases take priority over the automatic matching, so the names you set here always win.
          Switch to <strong>Unmapped</strong> to find the names that still fall back to a keyword search.
          {refsLoaded && azCategories.length > 0 && (
            <> {azCategories.length.toLocaleString("en-IN")} A–Z names and {brandedCategories.length} branded pages are available to map.</>
          )}
        </p>
      </div>

      {error && <AdminDataError message={error} onRetry={reload} />}

      {refsLoaded && brandedCategories.length === 0 && view === "unmapped" && (
        <div className="mb-5 flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-error-warning-line text-base" />
          </span>
          <p>
            The branded categories could not be loaded, so unmapped names can&apos;t be determined right now.
            Reload the page or try again in a moment.
          </p>
        </div>
      )}

      {view === "unmapped" && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3">
          <span className="flex items-center gap-2 text-xs font-semibold text-accent-900">
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-checkbox-multiple-line text-base" />
            </span>
            {selectedCount > 0 ? `${selectedCount} selected` : "Select names, pick a page, assign in bulk"}
          </span>
          <select
            value={bulkSlug}
            onChange={(e) => setBulkSlug(e.target.value)}
            className={`${adminInput} max-w-[260px] cursor-pointer`}
          >
            <option value="">Select a branded category…</option>
            {brandedCategories.map((c) => (
              <option key={c.slug} value={c.slug}>
                {c.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={selectedCount === 0 || !bulkSlug || bulkBusy}
            onClick={assignBulk}
            className={`${adminBtnPrimary} disabled:cursor-not-allowed disabled:opacity-50`}
          >
            {bulkBusy ? (
              <>
                <i className="ri-loader-4-line animate-spin text-sm" /> Assigning…
              </>
            ) : (
              <>
                <i className="ri-link-m text-sm" /> Assign {selectedCount > 0 ? selectedCount : ""}
              </>
            )}
          </button>
          {bulkMessage && (
            <span className="text-xs font-medium text-accent-900">{bulkMessage}</span>
          )}
          {selectedCount > 0 && (
            <button
              type="button"
              onClick={() => setSelected(new Set())}
              className="ml-auto cursor-pointer whitespace-nowrap text-xs font-semibold text-foreground-500 transition-colors hover:text-foreground-800"
            >
              Clear selection
            </button>
          )}
        </div>
      )}

      <div className={adminCard}>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : view === "aliases" ? (
          filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-background-100">
                <i className="ri-link-m text-2xl text-foreground-400" />
              </div>
              <p className="text-sm font-medium text-foreground-700">No category aliases yet</p>
              <p className="mt-1 text-xs text-foreground-500">
                Map an A–Z name to a branded page so it links through instead of running a keyword search.
              </p>
            </div>
          ) : (
            <div className="max-h-[640px] overflow-auto">
              <table className="w-full min-w-[760px] border-collapse">
                <thead className="sticky top-0 z-10 border-b border-background-200 bg-background-50">
                  <tr>
                    <th className={adminTh}>A–Z category name</th>
                    <th className={adminTh}>Links to</th>
                    <th className={adminTh}>Status</th>
                    <th className={`${adminTh} text-right`}>Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-background-100">
                  {filtered.map((a) => {
                    const targetName = a.categoryName ?? brandedNameBySlug.get(a.categorySlug) ?? null;
                    return (
                      <tr key={a.id} className="hover:bg-background-100/40">
                        <td className={adminTd}>
                          <div className="flex items-center gap-2.5">
                            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                              <i className="ri-bookmark-3-line text-base" />
                            </span>
                            <p className="font-semibold text-foreground-900">{a.azName}</p>
                          </div>
                        </td>
                        <td className={adminTd}>
                          <div className="flex flex-col">
                            <span className="font-medium text-foreground-800">
                              {targetName ?? "Unknown category"}
                            </span>
                            <code className="mt-0.5 w-fit rounded bg-background-100 px-1.5 py-0.5 text-[11px] text-foreground-500">
                              /category/{a.categorySlug}
                            </code>
                          </div>
                        </td>
                        <td className={adminTd}>
                          <button
                            type="button"
                            onClick={() => toggleActive(a)}
                            className={`inline-flex cursor-pointer items-center gap-1 rounded-full px-2.5 py-0.5 text-[10px] font-bold transition-colors ${
                              a.isActive
                                ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                : "bg-background-200 text-foreground-600 hover:bg-background-300"
                            }`}
                          >
                            {a.isActive ? "Active" : "Disabled"}
                          </button>
                        </td>
                        <td className={`${adminTd} text-right`}>
                          <div className="flex items-center justify-end gap-1">
                            <button type="button" onClick={() => openEdit(a)} className={adminIconBtn} title="Edit alias">
                              <i className="ri-pencil-line text-base" />
                            </button>
                            <button type="button" onClick={() => setDeleteTarget(a)} className={adminIconBtn} title="Delete alias">
                              <i className="ri-delete-bin-line text-base text-rose-600" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )
        ) : !refsLoaded || brandedCategories.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line animate-spin text-2xl text-primary-400" />
          </div>
        ) : filteredUnmapped.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
              <i className="ri-checkbox-circle-line text-2xl text-emerald-600" />
            </div>
            <p className="text-sm font-medium text-foreground-700">
              {search.trim() ? "No unmapped names match your search" : "Every A–Z name is mapped"}
            </p>
            <p className="mt-1 text-xs text-foreground-500">
              {search.trim()
                ? "Try a shorter keyword, or clear the search to see the full list."
                : "All A–Z categories link to a branded page or auto-match one. Nothing left to assign."}
            </p>
          </div>
        ) : (
          <div className="max-h-[640px] overflow-auto">
            <table className="w-full min-w-[720px] border-collapse">
              <thead className="sticky top-0 z-10 border-b border-background-200 bg-background-50">
                <tr>
                  <th className={`${adminTh} w-10`}>
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleSelectAll}
                      aria-label="Select all unmapped names"
                      className="h-4 w-4 cursor-pointer accent-primary-500"
                    />
                  </th>
                  <th className={adminTh}>A–Z category name</th>
                  <th className={adminTh}>Listings</th>
                  <th className={adminTh}>Currently</th>
                  <th className={`${adminTh} text-right`}>Assign</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-background-100">
                {filteredUnmapped.map((c) => {
                  const checked = selected.has(c.name);
                  return (
                    <tr
                      key={`${c.letter}-${c.name}`}
                      className={checked ? "bg-accent-50/70" : "hover:bg-background-100/40"}
                    >
                      <td className={adminTd}>
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSelect(c.name)}
                          aria-label={`Select ${c.name}`}
                          className="h-4 w-4 cursor-pointer accent-primary-500"
                        />
                      </td>
                      <td className={adminTd}>
                        <div className="flex items-center gap-2.5">
                          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-background-100 font-heading text-xs font-bold text-foreground-600">
                            {c.letter}
                          </span>
                          <p className="font-semibold text-foreground-900">{c.name}</p>
                        </div>
                      </td>
                      <td className={adminTd}>
                        <span className="text-sm text-foreground-600">
                          {c.businessCount.toLocaleString("en-IN")}
                        </span>
                      </td>
                      <td className={adminTd}>
                        <span className="inline-flex items-center gap-1.5 rounded-full bg-background-200 px-2.5 py-0.5 text-[10px] font-bold text-foreground-600">
                          <span className="flex h-3.5 w-3.5 items-center justify-center">
                            <i className="ri-search-line text-xs" />
                          </span>
                          Keyword search
                        </span>
                      </td>
                      <td className={`${adminTd} text-right`}>
                        <button
                          type="button"
                          onClick={() => assignOne(c.name)}
                          className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-accent-300 bg-accent-50 px-2.5 py-1.5 text-xs font-semibold text-accent-900 transition-colors hover:bg-accent-100"
                        >
                          <i className="ri-link-m text-sm" /> Assign
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing && (
        <AdminModal
          title={editId ? "Edit category alias" : "New category alias"}
          subtitle="Link an A–Z category name to a branded category page"
          onClose={() => setEditing(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setEditing(null)} className={adminBtnGhost}>
                Cancel
              </button>
              <button
                type="button"
                disabled={!canSubmit || busyId !== null}
                onClick={submit}
                className={adminBtnPrimary}
              >
                {editId ? "Save changes" : "Create alias"}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div>
              <label className={adminLabel} htmlFor="ca-az">A–Z category name</label>
              <input
                id="ca-az"
                type="text"
                list="ca-az-names"
                value={editing.azName ?? ""}
                onChange={(e) => setField("azName", e.target.value)}
                placeholder="Start typing, e.g. Advocates & Lawyers"
                className={adminInput}
                autoComplete="off"
              />
              <datalist id="ca-az-names">
                {azNames.map((name) => (
                  <option key={name} value={name} />
                ))}
              </datalist>
              <p className="mt-1.5 text-[11px] text-foreground-500">
                Pick a name from the A–Z list, or type any name to match exactly.
              </p>
            </div>

            <div>
              <label className={adminLabel} htmlFor="ca-target">Branded category page</label>
              <select
                id="ca-target"
                value={editing.categorySlug ?? ""}
                onChange={(e) => setField("categorySlug", e.target.value)}
                className={`${adminInput} cursor-pointer`}
              >
                <option value="">Select a branded category…</option>
                {brandedCategories.map((c) => (
                  <option key={c.slug} value={c.slug}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>

            {editing.azName?.trim() && (
              <div className="rounded-lg border border-background-200 bg-background-100 px-4 py-3 text-xs text-foreground-600">
                <span className="flex items-start gap-2">
                  <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
                    <i className={`${autoMatchForEditing ? "ri-sparkling-line" : "ri-search-line"} text-sm`} />
                  </span>
                  {autoMatchForEditing ? (
                    <span>
                      Without an alias, this name would auto-match to{" "}
                      <strong>{autoMatchForEditing.name}</strong>. Your alias overrides that.
                    </span>
                  ) : (
                    <span>
                      This name has no automatic match — the alias is what links it to a branded page.
                    </span>
                  )}
                </span>
              </div>
            )}

            <label className="flex cursor-pointer items-center gap-2.5 text-sm text-foreground-700">
              <input
                type="checkbox"
                checked={!!editing.isActive}
                onChange={(e) => setField("isActive", e.target.checked)}
                className="h-4 w-4 cursor-pointer accent-primary-500"
              />
              Active on the A–Z page
            </label>
          </div>
        </AdminModal>
      )}

      {deleteTarget && (
        <AdminModal
          title="Delete category alias"
          subtitle={deleteTarget.azName}
          onClose={() => setDeleteTarget(null)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setDeleteTarget(null)} className={adminBtnGhost}>
                Cancel
              </button>
              <button
                type="button"
                disabled={busyId === deleteTarget.id}
                onClick={confirmDelete}
                className={adminBtnDanger}
              >
                <i className="ri-delete-bin-line text-sm" /> Delete alias
              </button>
            </div>
          }
        >
          <p className="text-sm text-foreground-700">
            Delete the alias for <strong>{deleteTarget.azName}</strong>? This name will fall back to
            automatic matching (or a keyword search) instead.
          </p>
        </AdminModal>
      )}

      {importOpen && (
        <AdminModal
          title="Import category aliases"
          subtitle="Bulk create or update aliases from a CSV file"
          onClose={() => setImportOpen(false)}
          footer={
            <div className="flex justify-end gap-2">
              <button type="button" onClick={() => setImportOpen(false)} className={adminBtnGhost}>
                Close
              </button>
              <button
                type="button"
                disabled={validImportCount === 0 || importBusy}
                onClick={confirmImport}
                className={adminBtnPrimary}
              >
                {importBusy ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-sm" /> Importing…
                  </>
                ) : (
                  <>
                    <i className="ri-upload-2-line text-sm" />
                    Import {validImportCount > 0 ? validImportCount : ""}
                  </>
                )}
              </button>
            </div>
          }
        >
          <div className="space-y-4">
            <div className="rounded-lg border border-secondary-200 bg-secondary-50 px-4 py-3 text-xs text-secondary-900">
              <p>
                Upload a file with the columns <code className="rounded bg-background-100 px-1 py-0.5">az_name</code>,{" "}
                <code className="rounded bg-background-100 px-1 py-0.5">category_slug</code> and{" "}
                <code className="rounded bg-background-100 px-1 py-0.5">is_active</code>. Each slug must match an
                existing branded category. Names you already have are updated; new ones are created.
              </p>
              <button
                type="button"
                onClick={downloadTemplate}
                className="mt-2 cursor-pointer font-semibold text-secondary-900 underline-offset-2 hover:underline"
              >
                Download a blank template
              </button>
            </div>

            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-background-300 bg-background-50 px-4 py-6 text-sm font-semibold text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700">
              <span className="flex h-5 w-5 items-center justify-center">
                <i className="ri-upload-cloud-2-line text-lg" />
              </span>
              {importFileName || "Choose a CSV file"}
              <input type="file" accept=".csv,text/csv" onChange={handleImportFile} className="hidden" />
            </label>

            {importMessage && (
              <p className="text-xs font-semibold text-foreground-700">{importMessage}</p>
            )}

            {importRows.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2 text-xs">
                  <span className="inline-flex items-center rounded-full bg-emerald-100 px-2.5 py-0.5 font-bold text-emerald-700">
                    {validImportCount} valid
                  </span>
                  {invalidImportCount > 0 && (
                    <span className="inline-flex items-center rounded-full bg-rose-100 px-2.5 py-0.5 font-bold text-rose-700">
                      {invalidImportCount} skipped
                    </span>
                  )}
                </div>
                <div className="max-h-56 overflow-auto rounded-lg border border-background-200">
                  <table className="w-full min-w-[520px] border-collapse text-xs">
                    <thead className="sticky top-0 bg-background-100">
                      <tr>
                        <th className={adminTh}>A–Z name</th>
                        <th className={adminTh}>Branded page</th>
                        <th className={adminTh}>Active</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-100">
                      {importRows.slice(0, 100).map((r, i) => (
                        <tr key={`${r.azName}-${i}`} className={r.valid ? "" : "bg-rose-50/60"}>
                          <td className={adminTd}>
                            {r.azName || <span className="text-foreground-400">—</span>}
                          </td>
                          <td className={adminTd}>
                            {r.valid ? (
                              <span>{brandedNameBySlug.get(r.categorySlug) ?? r.categorySlug}</span>
                            ) : (
                              <span className="text-rose-600">{r.reason}</span>
                            )}
                          </td>
                          <td className={adminTd}>{r.isActive ? "Yes" : "No"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {importRows.length > 100 && (
                  <p className="text-[11px] text-foreground-500">
                    Showing the first 100 of {importRows.length} rows.
                  </p>
                )}
              </div>
            )}
          </div>
        </AdminModal>
      )}
    </div>
  );
}